import { getHomeworkAssignmentRoot } from "../homeworkAssignmentRoot";
import { useEffect, useRef, useState } from "react";
import { buildHomeworkReleaseEntries } from "../homeworkReleasePlan";
import useReleaseWriter from "./useReleaseWriter";
import useFirestore from "shared/hooks/useFirestore";
import { SelectedItem, branch, folder, setting, HomeworkSchedule } from "shared/types/Report";
import useGetBranchId from "shared/hooks/useGetBranchId";

const baseSetting:setting = {
    word:{
        focus:true,
        write:true,
        painting:true,
        word_repeat:1,
        example_repeat:1,
        word_write:1,
        cutline:3,
    },
    sentence:{
        cutline:3,
        listening:true,
        order:true,
        sentence_repeat:1,
        blank:10,
        pass:90,
        record:true,
    },
    video:{
        main_repeat:1,
        video:true,
        video_repeat:2
    }
}

interface Report {
    id:string,
    path:string,
    start:string,
  }
  
  interface EffectiveDate {
    start: string;
    end: string;
  }
  
  interface Assigned {
    effectiveDate: EffectiveDate;
    report: Report;
  }

  interface hasContent{
    word:boolean,
    sentence:boolean,
    video:boolean,
  }

interface Student {
    name: string;
    uid: string;
    status?: 'active' | 'suspended' | 'graduated';
    className?: string;
    lessonId?: string;
}

const useHomeworkSupplementaryReleaseView = () => {
    const now = new Date();
    const parseDate = (selected_date:Date) => {
        const month = (selected_date.getMonth() + 1).toString().length === 1 ? "0" + (selected_date.getMonth() + 1).toString() : selected_date.getMonth() + 1;
        const date = selected_date.getDate().toString().length === 1 ? "0" + selected_date.getDate().toString() : selected_date.getDate();
        return `${selected_date.getFullYear()}-${month}-${date}`
    }
    const {getBranchId} = useGetBranchId();
    const {readFirestore, readFirestoreOneDoc, readFirestoreWithSort} = useFirestore();

    const [selectedBranchList, setSelectedBranchList] = useState<string[]>([])
    const [folderList, setFolderList] = useState<folder[]>([]);
    const [assignedList, setAssignedList] = useState<Assigned[]>([]);
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<EffectiveDate>({
        start:parseDate(now),
        end:parseDate(now)
    })
    const [isContentAvailable, setIsContentAvailable] = useState<hasContent>();
    const [isOpenSettingWarningModal, setIsOpenSettingWarningModal] = useState(false);
    const [setting, setSetting] = useState<setting | undefined>(undefined);
    const [settingComplete, setSettingComplete] = useState<setting | undefined>(undefined);
    const [isOpenSettingModal, setIsOpenSettingModal] = useState<boolean>(false);
    const [isOpenReleaseSucceedModal, setIsOpenReleaseSucceedModal] = useState<boolean>(false);
    const [isOpenReleaseFailedModal, setIsOpenReleaseFailedModal] = useState<boolean>(false);
    const [isOpenNullModal, setIsOpenNullModal] = useState<boolean>(false);
    const [isOpenDateErrorModal, setIsOpenDateErrorModal] = useState<boolean>(false);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [modal, setModal] = useState({
        title:"",
        content:"",
        onConfirmClickHandler:() => {}
    })
    const [schedules, setSchedules] = useState<HomeworkSchedule[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [releaseFailureMessage, setReleaseFailureMessage] = useState("");
    const releaseInFlight = useRef(false);
    const { publish, retryPending, pendingReleaseCount } = useReleaseWriter("ReportAssignStudent");
    const [studentList, setStudentList] = useState<Student[]>([]);
    const [lessonList, setLessonList] = useState<{id: string, name: string}[]>([]);
    const [selectedLessonId, setSelectedLessonId] = useState<string>("");

    useEffect(() => {
        initializeData();
    }, [])

    useEffect(() => {
        getRootFolderList();
    }, [assignedList])

    const initializeData = async () => {
        setIsLoading(true);
        try {
            await getLessonList();
            await getAssignedList();
        } catch (error) {
            console.error('데이터 초기화 중 오류 발생:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getStudentList();
    }, [lessonList])
    
    const getLessonList = async() => {
        const branch_id = await getBranchId();
        const lesson_list = await readFirestore(`Lesson/Branch/${branch_id}`).then(res => {
            const list:branch[] = [];
            const doc_list = res.docs;
            const doc_list_length = res.docs.length;
            for(let i = 0; i < doc_list_length; i++){
                const lesson_id = doc_list[i].id;
                const lesson_name = doc_list[i].data().name;
                list.push({id:lesson_id, name:lesson_name})
            }
            return list;
        })
        setLessonList(lesson_list);
   
    }

    const getStudentList = async () => {
        setStudentList([]);
        const branch_id = await getBranchId();
        let allStudents: Student[] = [];
        // 레슨별 학생 할당 병렬 처리
        const lessonStudentPromises = lessonList.map(async (lesson) => {
            const res = await readFirestoreOneDoc(`Assign/${branch_id}/Lesson`, lesson.id);
            if (!res.exists()) return [];
            const student_list = Array.from(new Set<string>((res.data().students || []).filter((uid: unknown) => typeof uid === "string" && uid)));
            // 학생 정보 병렬 처리
            const studentInfoPromises = student_list.map(async (student_uid: string) => {
                const student_res = await readFirestoreOneDoc(`User/Auth/Student`, student_uid);
                if (!student_res.exists()) return null;
                const student_data = student_res.data();
                return {
                    name: student_data.name,
                    uid: student_uid,
                    className: lesson.name,
                    lessonId: lesson.id,
                };
            });
            const students = await Promise.all(studentInfoPromises);
            return students.filter(Boolean) as Student[];
        });
        const studentsByLesson = await Promise.all(lessonStudentPromises);
        allStudents = studentsByLesson.flat();
        allStudents.sort((a: Student, b: Student) => a.name.localeCompare(b.name));
        setStudentList(allStudents);
    }

    let toggle = false;
    let start = "";
    const onSelectReportClickHandler = (folder: folder, isChecked: boolean, parentStart?: string) => {
        // 개별 과제 선택 로직
        if (folder.type === "report" && folder.path) {
            setSelectedItems((prev: SelectedItem[]) => {
                if (isChecked) {
                    // 이미 선택된 과제인지 확인
                    const isAlreadySelected = prev.some(item => item.id === folder.id);
                    if (!isAlreadySelected) {
                        return [...prev, {
                            id: folder.id, 
                            start: parentStart ? parentStart : folder.path!, 
                            path: folder.path!, 
                            name: folder.name
                        }];
                    }
                } else {
                    // 선택 해제
                    return prev.filter(item => item.id !== folder.id);
                }
                return prev;
            });
        } else if (folder.type === "folder") {
            // 폴더 선택 시 하위 과제들을 모두 선택하거나 해제
            const getAllReportItems = (folder: folder): SelectedItem[] => {
                const items: SelectedItem[] = [];
                
                if (folder.subFolders) {
                    folder.subFolders.forEach(subFolder => {
                        if (subFolder.type === "report" && subFolder.path) {
                            items.push({
                                id: subFolder.id,
                                start: parentStart ? parentStart : subFolder.path,
                                path: subFolder.path,
                                name: subFolder.name
                            });
                        } else if (subFolder.type === "folder") {
                            items.push(...getAllReportItems(subFolder));
                        }
                    });
                }
                
                return items;
            };
            
            const allReportItems = getAllReportItems(folder);
            
            setSelectedItems((prev: SelectedItem[]) => {
                if (isChecked) {
                    // 폴더 선택 시 하위 과제들을 모두 추가 (중복 제거)
                    const existingIds = new Set(prev.map(item => item.id));
                    const newItems = allReportItems.filter(item => !existingIds.has(item.id));
                    return [...prev, ...newItems];
                } else {
                    // 폴더 해제 시 하위 과제들을 모두 제거
                    const folderItemIds = new Set(allReportItems.map(item => item.id));
                    return prev.filter(item => !folderItemIds.has(item.id));
                }
            });
        }
    };

    const getAssignedList = async() => {
        const branch_id = await getBranchId();
        const assigned_list = await readFirestore(`ReportAssignBranch/${branch_id}/Report`).then(res => {
            const list:Assigned[] = [];
            if(!res.empty){
                const doc_list = res.docs;
                for(const doc of doc_list){
                    const assigend = doc.data();
                    list.push({
                        report:assigend.report,
                        effectiveDate:assigend.effective_date
                    })
                }
            }
            return list;
        })
        setAssignedList(assigned_list);
    }

    const onSelectLessonClickHandler = (id:string, checked:boolean) => {
        setSelectedBranchList(prev => {
            if (checked) {
                return prev.includes(id) ? prev : [...prev, id];
            } else {
                return prev.filter(selectedId => selectedId !== id);
            }
        });
    }

    const getRootFolderList = async() => {
        setFolderList([]);
        const now = new Date();
        const validAssignments = assignedList.filter(assigned => 
            assigned.effectiveDate && parseDate(now) <= assigned.effectiveDate.end
        );

        if (validAssignments.length === 0) {
            setFolderList([]);
            return;
        }

        try {
            // Create a Set to track unique folder IDs
            const processedFolderIds = new Set<string>();
            
            // Batch all root folder queries
            const rootFolderPromises = validAssignments.map(async assigned => {
                try {
                    const report = assigned.report;
                    const { path: base_path, id: folder_id } = getHomeworkAssignmentRoot(report);
                    const folderKey = `${base_path}/${folder_id}`;
                    
                    // Skip if we've already processed this folder ID
                    if (processedFolderIds.has(folderKey)) return null;
                    processedFolderIds.add(folderKey);
                    
                    const prev_folder = await readFirestoreOneDoc(base_path, folder_id);
                    if(!prev_folder.exists()) return null;
                    
                    return {
                        id: folder_id,
                        path: base_path,
                        name: prev_folder.data().name,
                        type: prev_folder.data().type,
                        subFolders: prev_folder.data().type === "folder" ? [] : undefined
                    };
                } catch (error) {
                    console.error('폴더 정보 가져오기 중 오류:', error);
                    throw error;
                }
            });

            const rootFolders = (await Promise.all(rootFolderPromises))
                .filter(Boolean) as folder[];
            
            // Sort folders by name
            rootFolders.sort((a, b) => a.name.localeCompare(b.name));

            // Batch load all subfolders in parallel
            await Promise.all(rootFolders.map(async folder => {
                try {
                    if(folder.path && folder.type === "folder"){
                        folder.subFolders = await getFolderList(`${folder.path}/${folder.id}/Report`);
                    }
                } catch (error) {
                    console.error(`하위 폴더 로딩 중 오류 (${folder.id}):`, error);
                    throw error;
                }
            }));

            setFolderList(rootFolders);
        } catch (error) {
            console.error('루트 폴더 목록 가져오기 중 오류:', error);
            setFolderList([]);
            setSelectedItems([]);
            setSchedules([]);
            setModal({
                title: "ERROR",
                content: "과제 목록을 모두 불러오지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.",
                onConfirmClickHandler: () => setIsOpenModal(false),
            });
            setIsOpenModal(true);
        }
    }

    const getFolderList = async(path:string):Promise<folder[]> => {
        try {
            // Get all folders in one query
            const snapshot = await readFirestoreWithSort(path, {name:"name", direction:"desc"});
            if(snapshot.empty) return [];

            // Create a Set to track unique folder IDs
            const processedFolderIds = new Set<string>();
            
            const folders = snapshot.docs
                .map((doc: { id: string; data: () => any }) => {
                    if (processedFolderIds.has(doc.id)) return null;
                    processedFolderIds.add(doc.id);
                    
                    return {
                        id: doc.id,
                        name: doc.data().name,
                        type: doc.data().type,
                        path: path,
                        subFolders: doc.data().type === 'folder' ? [] : undefined,
                    };
                })
                .filter(Boolean) as folder[];

            // Batch load all subfolders in parallel
            await Promise.all(
                folders
                    .filter((folder: folder) => folder.type === 'folder')
                    .map(async (folder: folder) => {
                        try {
                            folder.subFolders = await getFolderList(`${path}/${folder.id}/Report`);
                        } catch (error) {
                            console.error(`하위 폴더 로딩 중 오류 (${folder.id}):`, error);
                            throw error;
                        }
                    })
            );

            return folders;
        } catch (error) {
            console.error(`폴더 목록 가져오기 중 오류 (${path}):`, error);
            throw error;
        }
    }

    const onEffectiveDateChangeHandler = (name:string, value:string) => {
        if(name === "end" && value < effectiveDate.start){
            setIsOpenDateErrorModal(true);
            return;
        }
        setEffectiveDate({
            ...effectiveDate,
            [name]:value
        })
    }


    const onDateErrorModalConfirmClickHandler = () => {
        setIsOpenDateErrorModal(false);
    }

    const onReportReleseClickHandler = async () => {
        if (releaseInFlight.current || isLoading) return;
        let entries;
        try {
            entries = buildHomeworkReleaseEntries({
                collection: "ReportAssignStudent",
                targetIds: selectedBranchList,
                selectedItems,
                schedules,
                startDate: effectiveDate.start,
                reportSetting: settingComplete,
            });
        } catch (error) {
            setModal({
                title: "ERROR",
                content: error instanceof Error ? error.message : "배포 설정을 확인해주세요.",
                onConfirmClickHandler: () => setIsOpenModal(false),
            });
            setIsOpenModal(true);
            return;
        }
        releaseInFlight.current = true;
        setIsLoading(true);
        try {
            if (await publish(entries)) setIsOpenReleaseSucceedModal(true);
        } catch (error) {
            setReleaseFailureMessage(error instanceof Error ? error.message : "과제 배포에 실패했습니다. 같은 화면에서 다시 시도해주세요.");
            setIsOpenReleaseFailedModal(true);
        } finally {
            releaseInFlight.current = false;
            setIsLoading(false);
        }
    };


    const onRetryReleaseClickHandler = async () => {
        if (releaseInFlight.current || isLoading) return;
        releaseInFlight.current = true;
        setIsLoading(true);
        setIsOpenReleaseFailedModal(false);
        try {
            if (await retryPending()) setIsOpenReleaseSucceedModal(true);
        } catch (error) {
            setReleaseFailureMessage(error instanceof Error ? error.message : "과제 배포에 실패했습니다. 다시 시도해주세요.");
            setIsOpenReleaseFailedModal(true);
        } finally {
            releaseInFlight.current = false;
            setIsLoading(false);
        }
    };

    const onSettingWarningModalOpenClickHanlder = () => {
        if(selectedItems.length){
            setIsOpenSettingWarningModal(true);
            return;
        }
        setIsOpenModal(true);
        setModal({
            title:"ERROR",
            content:"과제를 먼저 선택 해주세요",
            onConfirmClickHandler:() => setIsOpenModal(false)
        })
    }

    const onSettingWarningModalCloseClickHandler = () => {
        setIsOpenSettingWarningModal(false);
    }

    const onSettingWarningConfirmClickHandler = () => {
        openSettingModal();
    }


    const onDeleteSettingClickHandler = () => {
        setSetting(undefined);
        setSettingComplete(undefined);
    }

    const onCloseSettingModalClickHandler = () => {
        setSetting(settingComplete);
        setIsOpenSettingModal(false);
        setIsOpenSettingWarningModal(false);
    }

    const onConfirmSettingModalClickHandler = () => {
        setSettingComplete(setting);
        setIsOpenSettingWarningModal(false);
        setIsOpenSettingModal(false);
    }

    const openSettingModal = () => {
        if(!setting){
            setSetting(baseSetting);
        }
        checkHasNoContent();
        setIsOpenSettingModal(true);
    }

    const onNullModalConfirmClickHandler = () => {
        setIsOpenNullModal(false);
    }

    const onSucceedModalConfirmClickHandler = () => {
        setIsOpenReleaseSucceedModal(false);
    }

    const onFailedModalConfirmClickHandler = () => {
        setIsOpenReleaseFailedModal(false);
    }


    const checkHasNoContent = async () => {
        if (!selectedItems.length) return;
    
        try {
            // 모든 콘텐츠 확인을 병렬로 처리
            const contentPromises = selectedItems.map(async (item) => {
                try {
                    const res = await readFirestore(`${item.path}/${item.id}/Content`);
                    if (!res.empty) {
                        const id_list = res.docs.map((doc:any) => doc.id);
                        return {
                            hasWord: id_list.includes("word"),
                            hasSentence: id_list.includes("sentence"),
                            hasVideo: id_list.includes("video")
                        };
                    }
                    return { hasWord: false, hasSentence: false, hasVideo: false };
                } catch (error) {
                    console.error(`콘텐츠 확인 중 오류 (${item.id}):`, error);
                    return { hasWord: false, hasSentence: false, hasVideo: false };
                }
            });
            
            const results = await Promise.all(contentPromises);
            
            // 모든 결과를 종합하여 최종 상태 결정
            const hasWord = results.some(r => r.hasWord);
            const hasSentence = results.some(r => r.hasSentence);
            const hasVideo = results.some(r => r.hasVideo);
            
            setIsContentAvailable({
                word: hasWord,
                sentence: hasSentence,
                video: hasVideo
            });
        } catch (error) {
            console.error('콘텐츠 확인 중 오류 발생:', error);
            setIsContentAvailable({
                word: false,
                sentence: false,
                video: false
            });
        }
    };

    // studentList를 branchList 형태로 변환 (클래스명 + 학생이름, 클래스별 필터)
    const studentBranchList = Array.from(new Map(studentList
        .filter(student => !selectedLessonId || student.lessonId === selectedLessonId)
        .map(student => [student.uid, {
            id: student.uid,
            name: `${student.className ? student.className + ' ' : ''}${student.name}`,
        }])).values());

    return {
        branchList: studentBranchList,
        selectedBranchList,
        isContentAvailable,
        folderList,
        selectedItems,
        effectiveDate,
        onEffectiveDateChangeHandler,
        onSelectLessonClickHandler,
        onSelectReportClickHandler,
        studentList,
        lessonList,
        selectedLessonId,
        setSelectedLessonId,
        isOpenDateErrorModal,
        onDateErrorModalConfirmClickHandler,
        onReportReleseClickHandler,
        isOpenNullModal,
        setIsOpenNullModal,
        onNullModalConfirmClickHandler,
        isOpenReleaseSucceedModal,
        onSucceedModalConfirmClickHandler,
        isOpenReleaseFailedModal,
        onFailedModalConfirmClickHandler,
        setting,
        setSetting,
        settingComplete,
        isOpenSettingModal,
        onCloseSettingModalClickHandler,
        onConfirmSettingModalClickHandler,
        onDeleteSettingClickHandler,
        isOpenSettingWarningModal,
        onSettingWarningConfirmClickHandler,
        onSettingWarningModalCloseClickHandler,
        onSettingWarningModalOpenClickHanlder,
        isOpenModal,
        modal,
        schedules,
        setSchedules,
        isLoading,
        releaseFailureMessage, pendingReleaseCount, onRetryReleaseClickHandler
    }
}

export default useHomeworkSupplementaryReleaseView;
